import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { Equipment, Tier, TieredEquipment } from "../../models/item-data-models";
import { Filter } from "../../models/filter-models";
import { EquipmentTieringMode } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { RuleId } from "../../models/rule-id";
import { trackError } from "../../app.config";

export const EQUIPMENT_ORDERED_TIERS = [Tier.SPurple, Tier.ARed, Tier.BOrange, Tier.CYellow, Tier.DWhite, Tier.EVisible];

@Injectable({
  providedIn: 'root'
})
export class EquipmentTierService {
  private equipment: Equipment[];

  private defaultSubtleTieredEquipment!: TieredEquipment[];
  private defaultTierTieredEquipment!: TieredEquipment[];
  private tieredEquipment: TieredEquipment[] = [];

  constructor(private itemDataService: ItemDataService) {
    this.equipment = this.itemDataService.getEquipmentData();

    this.buildDefaultEquipmentTierListData('Subtle');
    this.buildDefaultEquipmentTierListData('Tiered');
  }

  buildDefaultEquipmentTierListData(mode: EquipmentTieringMode) {
    const data: TieredEquipment[] = [];

    this.equipment.forEach(metadata => {
      // Determine the tier for this equipment type based on mode
      let tier: Tier;
      if (mode === 'Subtle' && metadata.subtleTier) {
        tier = metadata.subtleTier;
      } else {
        tier = metadata.tieredTier;
      }

      data.push({
        equipmentType: metadata.equipmentType,
        selection: metadata.selection,
        slot: metadata.slot,
        category: metadata.category,
        displayName: metadata.displayName,
        tier,
      });
    });

    if (mode === 'Subtle') {
      this.defaultSubtleTieredEquipment = data;
    } else {
      this.defaultTierTieredEquipment = data;
    }
  }

  combineEquipmentTierListData(filter: Filter) {
    // Clone the appropriate default tier list based on the tiering mode
    const defaultList = filter.equipmentTieringMode === 'Subtle'
      ? this.defaultSubtleTieredEquipment
      : this.defaultTierTieredEquipment;

    const clone = structuredClone(defaultList) as TieredEquipment[];

    // Apply custom equipment tiers
    filter.customEquipmentTiers.forEach(customItem => {
      const existingItem = clone.find(item => item.equipmentType === customItem.equipmentType);
      if (existingItem) {
        existingItem.originalTier = existingItem.tier;
        existingItem.tier = customItem.tier;
      } else {
        trackError(new Error('Unexpected custom equipment: ' + customItem.equipmentType));
      }
    });

    this.tieredEquipment.length = 0;
    this.tieredEquipment.push(...clone);
  }

  getEquipmentTierData(): TieredEquipment[] {
    return this.tieredEquipment;
  }

  setTieringMode(mode: EquipmentTieringMode, filter: Filter) {
    filter.equipmentTieringMode = mode;
    this.combineEquipmentTierListData(filter);
  }

  getEquipmentTier(equipmentType: EquipmentType): Tier {
    const item = this.tieredEquipment.find(e => e.equipmentType === equipmentType);
    return item?.tier ?? Tier.EVisible;
  }

  getMetadataForEquipmentType(equipmentType: string): Equipment | undefined {
    return this.equipment.find(m => m.equipmentType === equipmentType);
  }

  getMetadataForRuleId(ruleId: RuleId): Equipment | undefined {
    return this.equipment.find(m => m.ruleId === ruleId);
  }
}

